/* ============================================================
   IVANDE WAITLIST QUIZ — full state machine
   assets/ivande-quiz.js

   Ports the reference 10-question flow:
     Scene 1 (landing) → Scene 2 (quiz, q1-q10) → Scene 3 (confirm)
   with reaction toast, progress whispers, smart size sync,
   birthday picker, tier reveal, ceremonial arrival.

   Backend wires:
     - window.IvandeAI.classifyArchetype(answers) — Phase 6 client
     - Klaviyo /client/subscriptions/ — uses settings.klaviyo_*
   Both fail gracefully — quiz completes either way.
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-iv-wait]');
  if (!root) return;

  const TOTAL_Q = 10;
  const ATELIER_CAP = parseInt(root.dataset.atelierCap, 10) || 200;
  const SIGNUP_BASE = parseInt(root.dataset.signupBase, 10) || 143;

  const state = {
    scene: 'landing',
    currentQ: 1,
    isTransitioning: false,
    isSplitOpen: false,
    answers: {
      archetype: null,
      saturday_uniform: null,
      palette: null,
      dressing_feeling: null,
      height: null,
      topSize: null,
      bottomSize: null,
      sizesSplit: false,
      fit_preference: null,
      work: 40, casual: 45, event: 15,
      wardrobeGap: '',
      firstName: '',
      email: '',
      birthdayMonth: null,
      birthdayDay: null,
      consentEmail: false,
      consentSms: false,
    },
  };

  /* ────────── Whispers (progress text) ────────── */
  const WHISPERS = {
    1:  'your taste, clarifying',
    2:  'a picture is forming',
    3:  'the palette finds you',
    4:  'your register, understood',
    5:  'now — the practical',
    6:  'so your first piece fits',
    7:  'the last of fit',
    8:  'your week, heard',
    9:  'one thought from you',
    10: 'where to send your invitation',
  };

  /* ────────── Reaction toasts (per answer) ────────── */
  const REACTIONS = {
    archetype: {
      quiet_luxury:   { msg: 'Quiet. A considered choice.' },
      soft_editorial: { msg: 'Softened — warm and deliberate.' },
      clean_modern:   { msg: 'Precise. Architectural.' },
      expressive:     { msg: 'Bolder. Distinctive.' },
    },
    saturday_uniform: {
      tailored_denim: { msg: 'Denim, well-cut.' },
      linen_set:      { msg: 'Linen — our favourite way to spend Saturday.' },
      slip_knit:      { msg: 'Softness, layered.' },
      trouser_tee:    { msg: 'Simple and precise.' },
    },
    palette: {
      bone_ivory:    { msg: 'Bone — a quiet register.' },
      earth_rust:    { msg: 'Earth tones. Considered.' },
      ink_graphite:  { msg: 'Ink. Always.' },
      sage_olive:    { msg: 'Sage — alive and understated.' },
    },
    dressing_feeling: {
      effortless:   { msg: 'Effortless is a discipline. Noted.' },
      intentional:  { msg: 'The best dressers are intentional.' },
      quiet:        { msg: 'Confidence without noise.' },
      expressive:   { msg: 'A signal. Heard.' },
    },
    height: { default: { msg: 'Your proportions, noted.' } },
    size:   { default: { msg: 'Noted. Your first piece will fit.' } },
    fit_preference: {
      slim:      { msg: 'Clean lines. Understood.' },
      regular:   { msg: 'True to size — as cut.' },
      relaxed:   { msg: 'Ease, considered.' },
      oversized: { msg: 'Deliberate scale.' },
    },
  };

  /* ────────── UK size → letter mapping ────────── */
  const UK_TO_LETTER = {
    '6': 'XS', '8': 'S', '10': 'M', '12': 'M',
    '14': 'L', '16': 'XL', '18': 'XXL', '20': 'XXL',
  };
  const formatSize = (uk) => uk ? `UK ${uk}` : '—';

  /* ────────── Archetype copy ────────── */
  const ARCHETYPES = {
    quiet_luxury: {
      name: 'Quiet Luxury',
      blurb: "You dress with patience — clothes that hold their shape, colours that don't shout, pieces that earn their place over seasons rather than trends. We'll send a first look tuned to this sensibility.",
    },
    soft_editorial: {
      name: 'Soft Editorial',
      blurb: 'A considered warmth — pieces that feel lived-in from the first wear, textures that catch the light, an aesthetic that reads as personal rather than polished. Your first look will lean here.',
    },
    clean_modern: {
      name: 'Clean Modern',
      blurb: 'Precision without coldness. Lines that hold, fabrics that drape, a wardrobe built on a few correct decisions rather than many good ones. We\'ll recommend accordingly.',
    },
    expressive: {
      name: 'Expressive',
      blurb: 'Clothes as a quiet signal — deliberate colour, intentional contrast, a willingness to let one piece lead. Your recommendations will feature our more distinctive cuts.',
    },
  };

  /* ────────── Tier next-steps copy ────────── */
  const STEPS = {
    atelier: [
      { title: 'Confirm',
        copy: 'Click the verification link in your inbox within 24 hours. This secures your Atelier Preview place.' },
      { title: 'A fabric swatch, by post',
        copy: "Ten days before launch, we'll send a linen swatch from the Summer collection to your door. We'll email shortly to confirm your address." },
      { title: 'First sight',
        copy: 'You see the full collection <strong>72 hours before Early Access</strong>. Quiet, considered, yours to browse first.' },
    ],
    early: [
      { title: 'Confirm',
        copy: 'Click the verification link in your inbox within 24 hours. This secures your place on the list.' },
      { title: 'The first look',
        copy: 'You see the Summer collection <strong>72 hours before public launch</strong> — a private window to consider your pieces.' },
      { title: 'A note before launch',
        copy: 'A letter from the founder arrives the morning of preview, with a considered look at what to pair and how it was made.' },
    ],
  };

  /* ────────── DOM refs ────────── */
  const scenes        = root.querySelectorAll('[data-scene]');
  const btnBack       = root.querySelector('[data-iv-back]');
  const progressFill  = root.querySelector('[data-iv-progress-fill]');
  const progressLabel = root.querySelector('[data-iv-progress-label]');
  const progressWhisp = root.querySelector('[data-iv-progress-whisper]');
  const reactionEl    = root.querySelector('[data-iv-reaction]');
  const toastEl       = root.querySelector('[data-iv-toast]');

  /* ────────── Scene switching ────────── */
  function goToScene(name) {
    scenes.forEach((s) => {
      const active = s.dataset.scene === name;
      if (active) {
        s.classList.add('is-active');
        requestAnimationFrame(() => s.classList.add('is-visible'));
      } else {
        s.classList.remove('is-visible');
        setTimeout(() => s.classList.remove('is-active'), 420);
      }
    });
    state.scene = name;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  /* ────────── Question navigation with slide ────────── */
  function showQuestion(n, direction = 'forward') {
    if (state.isTransitioning) return;
    if (n < 1 || n > TOTAL_Q) return;

    const current = root.querySelector('.iv-wait__q.is-active');
    const target  = root.querySelector(`.iv-wait__q[data-q="${n}"]`);
    if (!target || target === current) return;

    state.isTransitioning = true;
    const outClass = direction === 'forward' ? 'exit-left' : 'exit-right';
    const inClass  = direction === 'forward' ? 'enter-right' : 'enter-left';

    if (current) {
      current.classList.add(outClass);
      setTimeout(() => current.classList.remove('is-active', 'exit-left', 'exit-right'), 400);
    }

    setTimeout(() => {
      target.classList.add('is-active', inClass);
      setTimeout(() => {
        target.classList.remove('enter-right', 'enter-left');
        state.isTransitioning = false;
      }, 400);
    }, current ? 140 : 0);

    state.currentQ = n;
    progressFill.style.width = `${(n / TOTAL_Q) * 100}%`;
    progressLabel.textContent = `${String(n).padStart(2, '0')} / ${String(TOTAL_Q).padStart(2, '0')}`;

    progressWhisp.style.opacity = '0';
    setTimeout(() => {
      progressWhisp.textContent = WHISPERS[n] || '';
      progressWhisp.style.opacity = '1';
    }, 220);

    btnBack.classList.toggle('is-visible', n > 1);
  }

  function advance(delay = 420) {
    setTimeout(() => {
      if (state.currentQ < TOTAL_Q) showQuestion(state.currentQ + 1, 'forward');
    }, delay);
  }

  function goBack() {
    if (state.currentQ > 1) showQuestion(state.currentQ - 1, 'back');
    else goToScene('landing');
  }

  /* ────────── Reaction (italic Playfair toast, ~1.1s) ────────── */
  let reactionTimer = null;
  function showReaction(questionKey, value) {
    const bucket = REACTIONS[questionKey];
    if (!bucket) return;
    const r = bucket[value] || bucket.default;
    if (!r) return;
    showCustomReaction(r.msg);
  }
  function showCustomReaction(msg) {
    reactionEl.textContent = msg;
    reactionEl.classList.add('is-visible');
    clearTimeout(reactionTimer);
    reactionTimer = setTimeout(() => reactionEl.classList.remove('is-visible'), 1100);
  }

  /* ────────── Toast (validation feedback) ────────── */
  let toastTimer = null;
  function flash(msg) {
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2400);
  }

  /* ────────── Single-select grids (mood/text/chip) ────────── */
  root.querySelectorAll('[data-single]').forEach((grid) => {
    grid.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-value]');
      if (!opt) return;
      grid.querySelectorAll('[data-value]').forEach((o) => o.classList.remove('is-selected'));
      opt.classList.add('is-selected');
      const q = opt.closest('.iv-wait__q');
      const key = q.dataset.key;
      state.answers[key] = opt.dataset.value;
      showReaction(key, opt.dataset.value);
      if (q.dataset.auto === 'true') advance();
    });
  });

  /* ────────── Q6: smart size sync (UK primary) ────────── */
  const sizeMain   = root.querySelector('[data-role="size-main"]');
  const sizeTop    = root.querySelector('[data-role="size-top"]');
  const sizeBottom = root.querySelector('[data-role="size-bottom"]');
  const sizeConfirm= root.querySelector('[data-iv-size-confirm]');
  const sizeSplit  = root.querySelector('[data-iv-size-split]');
  const sizeTopRead= root.querySelector('[data-iv-size-top-read]');
  const sizeBotRead= root.querySelector('[data-iv-size-bot-read]');
  const splitToggle= root.querySelector('[data-iv-split-toggle]');

  let sizeAdvanceTimer = null;
  function scheduleSizeAdvance(delay = 800) {
    clearTimeout(sizeAdvanceTimer);
    sizeAdvanceTimer = setTimeout(() => {
      if (state.answers.topSize && state.answers.bottomSize) advance(0);
    }, delay);
  }
  function highlightChip(group, value) {
    group.querySelectorAll('.iv-wait__chip').forEach((c) => {
      c.classList.toggle('is-selected', c.dataset.value === value);
    });
  }

  if (sizeMain) sizeMain.addEventListener('click', (e) => {
    const c = e.target.closest('.iv-wait__chip');
    if (!c) return;
    const val = c.dataset.value;
    highlightChip(sizeMain, val);
    state.answers.topSize = val;
    state.answers.bottomSize = val;
    state.answers.sizesSplit = false;
    sizeTopRead.textContent = formatSize(val);
    sizeBotRead.textContent = formatSize(val);
    sizeConfirm.hidden = false;
    if (state.isSplitOpen) {
      highlightChip(sizeTop, val);
      highlightChip(sizeBottom, val);
    }
    showReaction('size', val);
    scheduleSizeAdvance();
  });

  if (sizeTop) sizeTop.addEventListener('click', (e) => {
    const c = e.target.closest('.iv-wait__chip');
    if (!c) return;
    highlightChip(sizeTop, c.dataset.value);
    state.answers.topSize = c.dataset.value;
    state.answers.sizesSplit = true;
    sizeTopRead.textContent = formatSize(c.dataset.value);
    if (state.answers.topSize && state.answers.bottomSize) scheduleSizeAdvance();
  });

  if (sizeBottom) sizeBottom.addEventListener('click', (e) => {
    const c = e.target.closest('.iv-wait__chip');
    if (!c) return;
    highlightChip(sizeBottom, c.dataset.value);
    state.answers.bottomSize = c.dataset.value;
    state.answers.sizesSplit = true;
    sizeBotRead.textContent = formatSize(c.dataset.value);
    if (state.answers.topSize && state.answers.bottomSize) scheduleSizeAdvance();
  });

  if (splitToggle) splitToggle.addEventListener('click', () => {
    clearTimeout(sizeAdvanceTimer);
    state.isSplitOpen = !state.isSplitOpen;
    sizeSplit.hidden = !state.isSplitOpen;
    splitToggle.textContent = state.isSplitOpen ? 'Same size for both' : 'Different for top & bottom?';
    if (state.isSplitOpen) {
      if (state.answers.topSize)    highlightChip(sizeTop, state.answers.topSize);
      if (state.answers.bottomSize) highlightChip(sizeBottom, state.answers.bottomSize);
    } else if (state.answers.topSize) {
      state.answers.bottomSize = state.answers.topSize;
      sizeBotRead.textContent = formatSize(state.answers.topSize);
      state.answers.sizesSplit = false;
    }
  });

  /* ────────── Q8: sliders ────────── */
  root.querySelectorAll('input[type="range"][data-store]').forEach((input) => {
    input.addEventListener('input', () => {
      state.answers[input.dataset.store] = +input.value;
      const label = root.querySelector(`[data-value-for="${input.id}"]`);
      if (label) label.textContent = `${input.value}%`;
    });
  });

  /* ────────── Q9, Q10: text/checkbox fields ────────── */
  root.querySelectorAll('input[data-store], textarea[data-store]').forEach((el) => {
    if (el.type === 'range') return;
    const update = () => {
      if (el.type === 'checkbox') state.answers[el.dataset.store] = el.checked;
      else state.answers[el.dataset.store] = el.value;
    };
    el.addEventListener('input', update);
    el.addEventListener('change', update);
  });

  /* ────────── Birthday opt-in (month → day) ────────── */
  const bdayTeaser   = root.querySelector('[data-action="toggle-birthday"]');
  const bdayPicker   = root.querySelector('[data-iv-bday-picker]');
  const bdayAction   = root.querySelector('[data-iv-bday-action]');
  const bdayMonth    = root.querySelector('[data-role="bday-month"]');
  const bdayDayStep  = root.querySelector('[data-iv-bday-day-step]');
  const bdayDayChips = root.querySelector('[data-iv-bday-day-chips]');
  const bdaySummary  = root.querySelector('[data-iv-bday-summary]');
  const bdayRead     = root.querySelector('[data-iv-bday-read]');

  const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS_IN_MONTH = [31,29,31,30,31,30,31,31,30,31,30,31];

  function buildDays(month) {
    bdayDayChips.innerHTML = '';
    const n = DAYS_IN_MONTH[month - 1];
    for (let d = 1; d <= n; d++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'iv-wait__chip iv-wait__chip--small';
      btn.dataset.value = d;
      btn.textContent = d;
      bdayDayChips.appendChild(btn);
    }
  }

  if (bdayTeaser) bdayTeaser.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="clear-birthday"]')) return;
    const open = bdayPicker.classList.toggle('is-open');
    bdayAction.textContent = open
      ? 'Close'
      : (state.answers.birthdayMonth && state.answers.birthdayDay ? 'Edit' : 'Add');
  });

  if (bdayMonth) bdayMonth.addEventListener('click', (e) => {
    const c = e.target.closest('.iv-wait__chip');
    if (!c) return;
    highlightChip(bdayMonth, c.dataset.value);
    state.answers.birthdayMonth = +c.dataset.value;
    state.answers.birthdayDay = null;
    buildDays(state.answers.birthdayMonth);
    bdayDayStep.hidden = false;
    bdaySummary.hidden = true;
  });

  if (bdayDayChips) bdayDayChips.addEventListener('click', (e) => {
    const c = e.target.closest('.iv-wait__chip');
    if (!c) return;
    highlightChip(bdayDayChips, c.dataset.value);
    state.answers.birthdayDay = +c.dataset.value;
    renderBirthdaySummary();
  });

  function renderBirthdaySummary() {
    if (state.answers.birthdayMonth && state.answers.birthdayDay) {
      bdayRead.textContent = `${MONTHS_EN[state.answers.birthdayMonth - 1]} ${state.answers.birthdayDay}`;
      bdaySummary.hidden = false;
      bdayAction.textContent = 'Edit';
    }
  }
  function clearBirthday() {
    state.answers.birthdayMonth = null;
    state.answers.birthdayDay = null;
    if (bdayMonth) bdayMonth.querySelectorAll('.iv-wait__chip').forEach((x) => x.classList.remove('is-selected'));
    if (bdayDayChips) bdayDayChips.innerHTML = '';
    if (bdayDayStep) bdayDayStep.hidden = true;
    if (bdaySummary) bdaySummary.hidden = true;
    if (bdayAction) bdayAction.textContent = 'Add';
    if (bdayPicker) bdayPicker.classList.remove('is-open');
  }

  /* ────────── Global click delegate ────────── */
  document.addEventListener('click', (e) => {
    if (!root.contains(e.target)) return;
    const t = e.target.closest('[data-action]');
    if (!t) return;
    const action = t.dataset.action;
    switch (action) {
      case 'start-quiz':
        goToScene('quiz');
        showQuestion(1, 'forward');
        break;
      case 'back':
        goBack();
        break;
      case 'advance': {
        const q = root.querySelector('.iv-wait__q.is-active');
        if (q && q.dataset.key === 'occasion_mix') showCustomReaction('Your week, noted.');
        else if (q && q.dataset.key === 'wardrobe_gap') showCustomReaction('Useful — thank you.');
        advance(0);
        break;
      }
      case 'advance-skip':
        state.answers.wardrobeGap = '';
        showCustomReaction('Onwards.');
        advance(0);
        break;
      case 'submit':
        handleSubmit();
        break;
      case 'clear-birthday':
        e.stopPropagation();
        clearBirthday();
        break;
      case 'resend':
        flash('Verification email resent — check your inbox.');
        break;
      case 'restart':
        location.reload();
        break;
    }
  });

  /* ────────── Keyboard: Escape / ArrowLeft for back ────────── */
  document.addEventListener('keydown', (e) => {
    if (state.scene !== 'quiz') return;
    const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName || '');
    if (e.key === 'Escape' || (e.key === 'ArrowLeft' && !typing)) goBack();
  });

  /* ────────── Submit ────────── */
  async function handleSubmit() {
    if (!state.answers.firstName.trim()) { flash("We'll need your first name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.answers.email)) {
      flash('Please enter a valid email address.'); return;
    }
    if (!state.answers.consentEmail) { flash('Please confirm to receive your invitation.'); return; }

    const btn = root.querySelector('[data-iv-submit]');
    btn.disabled = true;
    btn.textContent = 'Securing your place…';

    // Classify via the AI middleware (Phase 6 client). Falls back if unreachable.
    let aiArchetype = null;
    let aiBlurb = null;
    if (window.IvandeAI) {
      const answersForAi = Object.entries(state.answers)
        .filter(([k, v]) => v && typeof v === 'string')
        .map(([k, v]) => ({ question_id: k, option_value: v }));
      const result = await window.IvandeAI.classifyArchetype(answersForAi);
      if (result.ok && result.data) {
        aiArchetype = result.data.archetype || null;
        aiBlurb = result.data.welcome || null;
      }
    }

    // Subscribe to Klaviyo (no-ops if keys are blank — see settings.klaviyo_*).
    await submitToKlaviyo();

    // Determine position + tier
    const position = SIGNUP_BASE + 1;
    const tier = determineTier(position);

    // Populate confirmation scene
    root.querySelector('[data-iv-confirm-email]').textContent = state.answers.email;
    root.querySelector('[data-iv-confirm-name]').textContent = state.answers.firstName.trim();
    renderTierReveal(tier, position);
    renderNextSteps(tier);
    applyArchetypeCopy(aiArchetype, aiBlurb);

    goToScene('confirm');
    requestAnimationFrame(() => {
      staggerConfirmReveal();
      setTimeout(spawnCelebrationBurst, 500);
    });

    btn.disabled = false;
    btn.textContent = 'Reserve my place →';
  }

  function determineTier(position) {
    const forced = new URLSearchParams(location.search).get('tier');
    if (forced === 'early')   return { id: 'early',   position };
    if (forced === 'atelier') return { id: 'atelier', position };
    return { id: position <= ATELIER_CAP ? 'atelier' : 'early', position };
  }

  function renderTierReveal(tier, position) {
    const reveal = root.querySelector('[data-iv-tier-reveal]');
    const nameEl = root.querySelector('[data-iv-tier-name]');
    const posEl  = root.querySelector('[data-iv-tier-pos]');
    reveal.classList.toggle('is-atelier', tier.id === 'atelier');
    if (tier.id === 'atelier') {
      nameEl.textContent = 'Atelier Preview';
      posEl.innerHTML = `<strong>Position ${position}</strong> of the first ${ATELIER_CAP}`;
    } else {
      nameEl.textContent = 'Early Access';
      posEl.innerHTML = `<strong>Position ${position.toLocaleString('en-GB')}</strong> — the list before public launch`;
    }
  }

  function renderNextSteps(tier) {
    const container = root.querySelector('[data-iv-steps]');
    const steps = STEPS[tier.id];
    container.innerHTML = steps.map((s, i) => `
      <div class="iv-wait__step">
        <div class="iv-wait__step-num">${String(i + 1).padStart(2, '0')}</div>
        <div class="iv-wait__step-copy"><strong>${s.title}.</strong> ${s.copy}</div>
      </div>
    `).join('');
  }

  function applyArchetypeCopy(aiName, aiBlurb) {
    const local = ARCHETYPES[state.answers.archetype] || ARCHETYPES.quiet_luxury;
    const name = aiName || local.name;
    const blurb = aiBlurb || local.blurb;
    root.querySelector('[data-iv-archetype-name]').textContent = name;
    root.querySelector('[data-iv-archetype-blurb]').textContent = blurb;
  }

  function spawnCelebrationBurst() {
    const burst = root.querySelector('[data-iv-burst]');
    if (!burst) return;
    burst.innerHTML = '';
    const COUNT = 14;
    const rect = burst.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height * 0.22;
    for (let i = 0; i < COUNT; i++) {
      const dot = document.createElement('span');
      const angle = (Math.PI * 2 * i) / COUNT + (Math.random() * 0.4);
      const distance = 90 + Math.random() * 160;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance - 30;
      dot.style.left = `${cx}px`;
      dot.style.top  = `${cy}px`;
      dot.style.setProperty('--tx', `${tx}px`);
      dot.style.setProperty('--ty', `${ty}px`);
      dot.style.animationDelay = `${Math.random() * 200}ms`;
      burst.appendChild(dot);
    }
    setTimeout(() => (burst.innerHTML = ''), 2600);
  }

  function staggerConfirmReveal() {
    const content = root.querySelector('[data-iv-confirm]');
    if (!content) return;
    const blocks = content.querySelectorAll(':scope > *:not([data-iv-burst])');
    blocks.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(14px)';
      el.style.transition = 'opacity 700ms ease, transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1)';
      setTimeout(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 180 + i * 140);
    });
  }

  /* ────────── Klaviyo direct subscribe ────────── */
  async function submitToKlaviyo() {
    // Keys come from settings via data attributes on the section root.
    const key  = root.dataset.klaviyoKey || '';
    const list = root.dataset.klaviyoList || '';
    if (!key || !list) return false;

    const archetype = state.answers.archetype || '';
    const props = {
      ivande_archetype: archetype,
      ivande_top_size: state.answers.topSize,
      ivande_bottom_size: state.answers.bottomSize,
      ivande_top_size_letter: UK_TO_LETTER[state.answers.topSize] || null,
      ivande_bottom_size_letter: UK_TO_LETTER[state.answers.bottomSize] || null,
      ivande_height: state.answers.height,
      ivande_fit_preference: state.answers.fit_preference,
      ivande_wardrobe_gap: state.answers.wardrobeGap,
      ivande_consent_sms: state.answers.consentSms,
    };
    if (state.answers.birthdayMonth && state.answers.birthdayDay) {
      props.ivande_birthday_month = state.answers.birthdayMonth;
      props.ivande_birthday_day = state.answers.birthdayDay;
    }

    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: state.answers.email,
                    first_name: state.answers.firstName,
                    properties: props,
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }
})();
